import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { AssignCoordinatorPayloadInterface } from '@interfaces/assign-coordinator-payload.interface';
import { BaseDropdownInterface } from '@interfaces/base-dropdown.interface';
import { LegAssignmentInterface } from '@interfaces/leg-assignment.interface';
// import { ServiceRequestFlagResponse, ServiceRequestFlagUpdateFollowupTimePayload, ServiceRequestFlagUpdateResolveFlagPayload } from '@interfaces/service-request/service-request-flag.interface';
import { IFilterDates } from 'src/app/common/models/executive-dashbaord-model';
import { PreviewCancellationReasonInterface } from '../MaterialDialogs/reason-box-dialog/interfaces/preview-cansellation.interface';
import { IAssignmentLogDetailsViewModel } from '../model/assignmentLog.model';
import { ISrCancellationDetails } from '../model/common.model';
import { IApproveOrRejectModel } from '../model/customerSRQueueModel';
import {
  DestinationWithTripViewModel,
  ISrStatusChangeViewModel,
  LegViewModel,
  ServiceRequestInfo,
} from '../model/report.model';
import {
  IFilteredSRResponse,
  ILegTimeStampViewModel,
  IServiceRequestInfo,
  ISRCancellationReasonViewModel,
  ISRCancellationStepViewModel,
} from '../model/service-request-model';
import { ITeamAndFlightDetailViewModel } from '../model/team-member.interface';
import { IVendorModel } from '../model/Vendor.model';
import { FinancialStatusList } from './customer.model';
import { filterData, filterServiceRequestData, IServiceRequestSelectedFilterValues } from './filterData.model';
import { GroupInvoicePayload, ServiceRequestGroupInvoicePayload } from './generateInvoiceView.model';
import {
  addEquipment,
  CreateServiceRequest,
  GetAllTissueModel,
  GetServiceRequestModel,
  IAdditionalEquipmentTypeModel,
  ICreatedByNameModel,
  ICreateSRLinkPayload,
  IFilterContentResponse,
  IOrganTypeMappingViewModel,
  IServiceRequestLogs,
  IServiceRequestStatusList,
  IServiceRequestStepLogs,
  IUnlinkSRPayload,
  JobType,
  SaveAsServiceRequestTemplate,
  ServiceRequestTemplate,
  ServiceType,
  TripIdViewModel,
} from './service-request';
import {
  ServiceRequestFlagResponse,
  ServiceRequestFlagUpdateFollowupTimePayload,
  ServiceRequestFlagUpdateResolveFlagPayload
} from '@modals/service-request-flags/interfaces/service-request-flag.interface';
import { AssignBulkCoordinatorPayloadInterface } from '@interfaces/assign-bulk-coordinator-payload.interface';
import { AssignBulkCoordinatorResponse } from '@interfaces/assign-bulk-coordinator-response.interface';
import { IFiletBasedSearchKeyword } from '@interfaces/filet-based-search-keyword.interface';

@Injectable({
  providedIn: 'root',
})
export class ServiceRequestService {
  // todo avoid using the additional Subjects as selectedSRToLinkSubject$, selectedSRToLinkAction ...
  public selectedSRToLinkSubject$ = new BehaviorSubject<IServiceRequestInfo[]>([]);
  public selectedSRToLinkAction = this.selectedSRToLinkSubject$.asObservable();
  public srServiceTypeList$ = new BehaviorSubject<ServiceType[]>([]);
  public serviceRequestStatusList$ = new BehaviorSubject<IServiceRequestStatusList[]>([]);
  public coordinatorList$ = new BehaviorSubject<BaseDropdownInterface[]>([]);
  public serviceRequestFilter$ = new BehaviorSubject<IServiceRequestSelectedFilterValues>(null);
  public legRecordUpdate = new Subject<any>();
  public refreshServieRequestList$ = new Subject<any>();

  private readonly cateringDietaryRequirements: string[] = [
    'Vegetarian', 'Vegan', 'Religious restrictions (add details in notes)', 'No gluten',
    'No dairy', 'Pescatarian', 'Food allergies', 'Other dietary restrictions',
  ];

  constructor(private httpClient: HttpClient) {}

  timZoneChange = new Subject<any>();
  isTimeZoneLockSubject$ = new BehaviorSubject<boolean>(false);
  isTimeZoneLockAction$ = this.isTimeZoneLockSubject$.asObservable();
  currentTimeZone = new Subject<any>();

  calculateFinalDestination(serviceRequestInfos: ServiceRequestInfo[]): Promise<DestinationWithTripViewModel[]> {
    const finalDestination = [];
    // todo need to refactor using Promise; it should be Observable
    return new Promise((resolve) => {
      serviceRequestInfos.forEach(x => {
        const fDestination = x.destination[x.destination.length - 1];
        let lastDestination: DestinationWithTripViewModel = {
          serviceRequestId: x.serviceRequestId,
          destination: '',
          destinationAddress: '',
          destinationAddressId: '',
          destinationAddressLocation: '',
          destinationSequenceNumber: 0,
        };
        if (fDestination !== undefined) {
          lastDestination = {
            serviceRequestId: x.serviceRequestId,
            destination: fDestination.destination,
            destinationAddress: fDestination.destinationAddress,
            destinationAddressId: fDestination.destinationAddressId,
            destinationAddressLocation: fDestination.destinationAddressLocation,
            destinationSequenceNumber: fDestination.destinationSequenceNumber,
          };
        }
        finalDestination.push(lastDestination);
      });
      resolve(finalDestination);
    });
  }

  // todo need to avoid using type any
  splitLegListByServiceRequestId(legList: any[], serviceRequestId: string) {
    const result = legList.reduce((acc, obj) => {
      const key = obj[serviceRequestId];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});

    for (const key in result) {
      if (key) {
        result[key].sort((a, b) => new Date(a.pickUpTime).getTime() - new Date(b.pickUpTime).getTime());
      }
    }

    return result;
  }

  getServiceRequests(businessUnitId: string, offset, limit): Observable<any> {
    const url = `${environment.apiUrl}/Customer/GetAllServiceRequests/${businessUnitId}/${offset}/${limit}`;

    return this.httpClient.get(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllActiveRequestCount(filter: IFilterDates): Observable<string> {
    const url = `${environment.apiUrl}/ExecutiveDashboard/getAllActiveRequestCount`;

    return this.httpClient.post<string>(url, filter)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getExecuteRequests(filter: IFilterDates): Observable<any> {
    const url = `${environment.apiUrl}/ExecutiveDashboard/FetchCardDataCount`;

    return this.httpClient.post(url, filter)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getActiveSR(filter: IFilterDates): Observable<any> {
    const url = `${environment.apiUrl}/ExecutiveDashboard/FetchActiveRequestList`;

    return this.httpClient.post(url, filter)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getTripReportData(srId): Observable<any> {
    const url = `${environment.apiUrl}/ExecutiveDashboard/FetchTripReportByServiceRequestId/${srId}`;

    return this.httpClient.get(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllJobById(serviceTypeId: string): Observable<JobType[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetAllJobById/${serviceTypeId}`;

    return this.httpClient.get<JobType[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // todo typo mistake for method name, will be fixed later
  getdropOffLocations(serviceTypeId: string): Observable<ServiceType[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetAllServiceTypeDropOffLocationById/${serviceTypeId}`;

    return this.httpClient.get<ServiceType[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getPersonalServices(serviceTypeId: string): Observable<ServiceType[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetAllPersonalServiceById/${serviceTypeId}`;

    return this.httpClient.get<ServiceType[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getServiceTypes(): Observable<ServiceType[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetAllServiceType`;

    return this.httpClient.get<ServiceType[]>(url)
      .pipe(
        tap((serviceTypeList: ServiceType[]) => {
          // todo avoid using the additional Subject srServiceTypeList$
          this.srServiceTypeList$.next(serviceTypeList);
        }),
        catchError((error: HttpErrorResponse) => throwError(() => error))
      );
  }

  addServiceRequestData(createServiceRequest: CreateServiceRequest): Observable<any> {
    const url = `${environment.apiUrl}/ServiceRequest/CreateServiceRequest`;

    return this.httpClient.post(url, createServiceRequest)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllStatus(): Observable<IServiceRequestStatusList[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetAllStatus`;

    return this.httpClient.get<IServiceRequestStatusList[]>(url)
      .pipe(
        tap((resp: IServiceRequestStatusList[]) => {
          // todo avoid using the additional Subject serviceRequestStatusList$
          this.serviceRequestStatusList$.next(resp);
        }),
        catchError((error: HttpErrorResponse) => throwError(() => error))
      );
  }

  getAllTissueId(memberID): Observable<GetAllTissueModel[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetAllTissueId/${memberID}`;

    return this.httpClient.get<GetAllTissueModel[]>(url)
      .pipe(
        map((response: GetAllTissueModel[]) =>
          response.filter((tissue: GetAllTissueModel) => tissue.tissueId !== '')),
        catchError((error: HttpErrorResponse) => throwError(() => error))
      );
  }

  getServiceRequestTemplate(): Observable<GetServiceRequestModel[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetServiceRequestTemplate`;

    return this.httpClient.get<GetServiceRequestModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFilteredServiceRequestData(filter: filterServiceRequestData, timezoneOffset, isCustomerSR: boolean): Observable<IFilteredSRResponse> {
    const url = `${environment.apiUrl}/ServiceRequest/FilterServiceRequest/${timezoneOffset}/${isCustomerSR}`;

    return this.httpClient.post<IFilteredSRResponse>(url, filter)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  saveAsServiceRequestTemplate(
    saveAsServiceRequestTemplate: SaveAsServiceRequestTemplate,
  ): Observable<any> {
    const url = `${environment.apiUrl}/ServiceRequest/SaveAsTemplateServiceRequest`;

    return this.httpClient.post(url, saveAsServiceRequestTemplate)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getServiceRequestData(serviceRequestId: string): Observable<ServiceRequestTemplate> {
    const url = `${environment.apiUrl}/ServiceRequest/GetServiceRequestTemplateById/${serviceRequestId}`;

    return this.httpClient.get<ServiceRequestTemplate>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getServiceRequestInvoiceDetail(serviceRequestId: GroupInvoicePayload): Observable<any> {
    const url = `${environment.apiHost}/FinanceSrGroup/InvoiceGroupDetailByServiceRequestId`;

    return this.httpClient.post(url, serviceRequestId)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFinanceStatus(): Observable<FinancialStatusList[]> {
    const url = `${environment.apiHost}/AppCategory/FinanceStatus`;

    return this.httpClient.get<FinancialStatusList[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  saveServiceRequestInvoice(financeData: ServiceRequestGroupInvoicePayload): Observable<any> {
    // todo typo mistake on endpoint name, will contact with backend developers
    const url = `${environment.apiHost}/FinanceSrGroup/SaveServicerequestInvoice`;

    return this.httpClient.post(url, financeData)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  editServiceRequest(
    serviceRequestId: string,
    createServiceRequest: CreateServiceRequest,
  ): Observable<any> {
    const url = `${environment.apiUrl}/ServiceRequest/EditServiceRequest/${serviceRequestId}`;

    return this.httpClient.put(url, createServiceRequest)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFilterLegData(filter: filterData): Observable<IVendorModel[]> {
    const url = `${environment.apiHost}/BusinessUnit/vendorFilter`;

    return this.httpClient.post<IVendorModel[]>(url, filter)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFilterData(memberID): Observable<IFilterContentResponse[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetFilterContent/${memberID}`;

    return this.httpClient.get<IFilterContentResponse[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFilterDataBasedOnSearchKeyWord(searchKeyword: string, memberID: string): Observable<IFiletBasedSearchKeyword[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetFilterIdBasedOnSearchKeyWord/${memberID}/${searchKeyword}`;

    return this.httpClient.get<IFiletBasedSearchKeyword[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  addEquipment(payload: addEquipment): Observable<string> {
    const url = `${environment.apiUrl}/ServiceRequest/CreateEquipmentType`;

    return this.httpClient.post<string>(url, payload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  deleteJobType(jobTypeId): Observable<boolean> {
    // todo typo mistake for url path, will be fixed later, will contact with BE developers
    const url = `${environment.apiUrl}/ServiceRequest/DeleteSaerviceType/${jobTypeId}`;

    return this.httpClient.delete<boolean>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  GetSRDataById(id: string): Observable<ISrStatusChangeViewModel> {
    const url = `${environment.apiUrl}/ServiceRequest/GetSRDataById/${id}`;

    return this.httpClient.get<ISrStatusChangeViewModel>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getTripId(): Observable<TripIdViewModel[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetTripId`;

    return this.httpClient.get<TripIdViewModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getLegDetail(serviceRequestIds, vendorId?: string): Observable<LegViewModel[]> {
    const url = `${environment.apiHost}/leg/legList/${vendorId ?? ''}`;

    return this.httpClient.post<LegViewModel[]>(url, serviceRequestIds)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  addDescriptionInSR(id: string, data): Observable<string> {
    const url = `${environment.apiUrl}/ServiceRequest/AddServiceRequestDescription/${id}`;

    return this.httpClient.post<string>(url, data)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getPreviewCancellationReason(serviceRequestId: string): Observable<PreviewCancellationReasonInterface> {
    const url = `${environment.apiUrl}/ServiceRequest/GetCancellationReason/${serviceRequestId}`;

    return this.httpClient.get<PreviewCancellationReasonInterface>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  undoPreCancelServiceRequest(serviceRequestId: string): Observable<boolean> {
    const url = `${environment.apiUrl}/ServiceRequest/UndoPreCancelServiceRequest/${serviceRequestId}`;

    return this.httpClient.get<boolean>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  preCancelServiceRequest(cancellationDetails: ISrCancellationDetails): Observable<ISrCancellationDetails> {
    const url = `${environment.apiUrl}/ServiceRequest/PreCancelServiceRequest`;

    return this.httpClient.post<ISrCancellationDetails>(url, cancellationDetails)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  cancelServiceRequest(cancellationDetails: ISrCancellationDetails): Observable<ISrCancellationDetails> {
    const url = `${environment.apiUrl}/ServiceRequest/CancelServiceRequest`;

    return this.httpClient.post<ISrCancellationDetails>(url, cancellationDetails)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getSrCancellationDetails(srCancellationReasonId: string): Observable<ISrCancellationDetails> {
    const url = `${environment.apiUrl}/ServiceRequest/GetSrCancellationDetails/${srCancellationReasonId}`;

    return this.httpClient.get<ISrCancellationDetails>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // todo typo mistake for method name, will be fixed later
  unCancleServiceRequest(srId: string): Observable<string> {
    const url = `${environment.apiUrl}/ServiceRequest/UnCancelServiceRequest/${srId}`;

    return this.httpClient.get<string>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getEquipmentType(id): Observable<string> {
    const url = `${environment.apiUrl}/ServiceRequest/GetAllJobById/${id}`;

    return this.httpClient.get<string>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // todo typo mistake for method name, will be fixed later
  getOrgenType(id): Observable<JobType[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetAllJobById/${id}`;

    return this.httpClient.get<JobType[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getSpecimenType(id): Observable<string> {
    // todo the same url as for getOrgenType
    const url = `${environment.apiUrl}/ServiceRequest/GetAllJobById/${id}`;

    return this.httpClient.get<string>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getTeamType(id): Observable<string> {
    // todo the same url as for getOrgenType, getSpecimenType
    const url = `${environment.apiUrl}/ServiceRequest/GetAllJobById/${id}`;

    return this.httpClient.get<string>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getDropOffLocation(id): Observable<any> {
    const url = `${environment.apiUrl}/ServiceRequest/GetAllServiceTypeDropOffLocationById/${id}`;

    return this.httpClient.get(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getLegTimeStamp(id): Observable<ILegTimeStampViewModel> {
    const url = `${environment.apiHost}/leg/FetchTimeStamp/${id}`;

    return this.httpClient.get<ILegTimeStampViewModel>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  updateTimeStamp(id, updatedData): Observable<string> {
    const url = `${environment.apiHost}/leg/UpdateLegTimeStamp/${id}`;

    return this.httpClient.post<string>(url, updatedData)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getServiceRequestLogs(serviceRequestId): Observable<IServiceRequestLogs[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetServiceRequestLogs/${serviceRequestId}`;

    return this.httpClient.get<IServiceRequestLogs[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getServiceRequestStepsLogs(serviceRequestId): Observable<IServiceRequestStepLogs[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetServiceRequestStepLogs/${serviceRequestId}`;

    return this.httpClient.get<IServiceRequestStepLogs[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getJobDone(serviceRequestId): Observable<boolean> {
    const url = `${environment.apiUrl}/ServiceRequest/UpdateServiceRequestStatus/${serviceRequestId}`;

    return this.httpClient.get<boolean>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getTripIdByServiceRequestList(srIdList: GroupInvoicePayload): Observable<TripIdViewModel[]> {
    const url = `${environment.apiHost}/AppCategory/TripIdList`;

    return this.httpClient.post<TripIdViewModel[]>(url, srIdList)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  fetchCoordination(): Observable<BaseDropdownInterface[]> {
    const url = `${environment.apiHost}/MemberRole/fetchInternalCoordinator`;

    return this.httpClient.get<BaseDropdownInterface[]>(url)
      .pipe(
        tap((coordinatorList: BaseDropdownInterface[]) => {
          // todo avoid using the additional Subject coordinatorList$
          this.coordinatorList$.next(coordinatorList);
        }),
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  assignCoordinatorInSr(data: AssignCoordinatorPayloadInterface): Observable<boolean> {
    const url = `${environment.apiUrl}/ServiceRequest/AssignCoordinatorInSR`;

    return this.httpClient.put<boolean>(url, data)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  assignBulkCoordinatorInSr(data: AssignBulkCoordinatorPayloadInterface): Observable<AssignBulkCoordinatorResponse> {
    const url = `${environment.apiUrl}/ServiceRequest/BulkAssignCoordinator`;

    return this.httpClient.put<AssignBulkCoordinatorResponse>(url, data)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getOrganTypes(): Observable<IOrganTypeMappingViewModel[]> {
    const url = `${environment.apiHost}/AppCategory/GetOrganTypes`;

    return this.httpClient.get<IOrganTypeMappingViewModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  addAdditionalEquipmentType(additionalEquipmentType: addEquipment): Observable<string> {
    const url = `${environment.apiUrl}/ServiceRequest/CreateAdditionalEquipmentType`;

    return this.httpClient.post<string>(url, additionalEquipmentType)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAdditionalEquipmentTypes(): Observable<IAdditionalEquipmentTypeModel[]> {
    const url = `${environment.apiHost}/appCategory/GetAdditionalEquipmentTypes`;

    return this.httpClient.get<IAdditionalEquipmentTypeModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  approveOrRejectServiceRequest(approveOrRejectSRData: IApproveOrRejectModel): Observable<boolean> {
    const url = `${environment.apiUrl}/ServiceRequest/ApproveOrRejectServiceRequest`;

    return this.httpClient.post<boolean>(url, approveOrRejectSRData)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getCreatedByNameList(): Observable<ICreatedByNameModel[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetCreatedByNameList`;

    return this.httpClient.get<ICreatedByNameModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  createSRLink(linkSRPayload: ICreateSRLinkPayload): Observable<boolean> {
    const url = `${environment.apiUrl}/ServiceRequest/CreateServiceRequestLink`;

    return this.httpClient.post<boolean>(url, linkSRPayload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  unlinkSR(unlinkSRPayload: IUnlinkSRPayload): Observable<boolean> {
    const url = `${environment.apiUrl}/ServiceRequest/UnlinkServiceRequest`;

    return this.httpClient.post<boolean>(url, unlinkSRPayload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getSRLinkIdList(): Observable<string[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetServiceRequestLinkIdList`;

    return this.httpClient.get<string[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getTripIdListForSRLinking(): Observable<string[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetTripIdListForSRLinking`;

    return this.httpClient.get<string[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getSRIdListBySRLinkId(serviceRequestLinkId: string, customerBusinessUnitId: string): Observable<string[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetServiceRequestIdListBySRLinkId/${serviceRequestLinkId}/${customerBusinessUnitId}`;

    return this.httpClient.get<string[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // todo typo mistake, will be fixed later
  getSRcancellationStep(): Observable<ISRCancellationStepViewModel[]> {
    const url = `${environment.apiHost}/AppCategory/SRCancellationStepType`;
    return this.httpClient.get<ISRCancellationStepViewModel[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  // todo typo mistake, will be fixed later
  getSRcancellationReason(): Observable<ISRCancellationReasonViewModel[]> {
    const url = `${environment.apiHost}/AppCategory/SRCancellationReasonType`;
    return this.httpClient.get<ISRCancellationReasonViewModel[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getTeamAndFlightDetails(serviceRequestId: string): Observable<ITeamAndFlightDetailViewModel> {
    const url = `${environment.apiUrl}/ServiceRequest/GetTeamAndFlightDetails/${serviceRequestId}`;
    return this.httpClient.get<ITeamAndFlightDetailViewModel>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAssignmentLogDetails(serviceRequestId: string): Observable<IAssignmentLogDetailsViewModel[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetAssignmentLogDetails/${serviceRequestId}`;

    return this.httpClient.get<IAssignmentLogDetailsViewModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getVendorList(serviceRequestId: string): Observable<LegAssignmentInterface[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetLegAssignments/${serviceRequestId}/leg-assignments`;

    return this.httpClient.get<LegAssignmentInterface[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getCateringDietaryRequirements(): Observable<string[]> {
    return of(this.cateringDietaryRequirements);
  }

  getTripsBasedOnTripNumber(): Observable<string[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetTripsBasedOnTripNumber`;

    return this.httpClient.get<string[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  fetchManagers(): Observable<BaseDropdownInterface[]> {
    const url = `${environment.apiHost}/MemberRole/fetchManagers`;

    return this.httpClient.get<BaseDropdownInterface[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getServiceRequestFlags(srId: string): Observable<ServiceRequestFlagResponse> {
    const url = `${environment.apiUrl}/SrFlag/${srId}`;

    return this.httpClient.get<any>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
  createServiceRequestFlag(payload: any): Observable<any> {
    const url = `${environment.apiUrl}/SrFlag/`;

    return this.httpClient.post<any>(url, payload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  resolveServiceRequestFlag(payload: ServiceRequestFlagUpdateResolveFlagPayload): Observable<any> {
    const url = `${environment.apiUrl}/SrFlag/ResolveFlag`;

    return this.httpClient.put<ServiceRequestFlagUpdateResolveFlagPayload>(url, payload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  updateServiceRequestFlagFollowupTime(payload: ServiceRequestFlagUpdateFollowupTimePayload): Observable<any> {
    const url = `${environment.apiUrl}/SrFlag/updateFollowupTime`;

    return this.httpClient.put<ServiceRequestFlagUpdateFollowupTimePayload>(url, payload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  triggerRefresh(): void {
    this.refreshServieRequestList$.next('');
  }
}
